import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { WorkflowDetalhe, WorkflowEtapa, WorkflowEtapaTipo } from '../../core/models/workflow.models';
import { WorkflowsService } from '../../core/services/workflows.service';

type StatusFilter = 'TODOS' | 'EM_ANDAMENTO' | 'CONCLUIDO' | 'ATRASADO';

interface StageSummary {
  etapa: string;
  items: WorkflowDetalhe[];
  wip: number;
  atrasados: number;
  parados: number;
  idadeMedia: number;
  criticos: WorkflowDetalhe[];
}

interface PersonSummary {
  responsavel: string;
  items: WorkflowDetalhe[];
  wip: number;
  atrasados: number;
  parados: number;
  etapaTop: string;
}

interface DailyCount {
  date: string;
  count: number;
}

@Component({
  selector: 'app-relatorios-gestao-wip-capacidade',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './relatorios-gestao-wip-capacidade.component.html',
  styleUrl: './relatorios-gestao-wip-capacidade.component.scss',
})
export class RelatoriosGestaoWipCapacidadeComponent implements OnInit, OnDestroy {
  workflows: WorkflowDetalhe[] = [];
  filtered: WorkflowDetalhe[] = [];
  wipItems: WorkflowDetalhe[] = [];

  filterStart = '';
  filterEnd = '';
  filterSeguradora = 'TODOS';
  filterResponsavel = 'TODOS';
  filterStatus: StatusFilter = 'TODOS';
  onlyWip = true;
  paradoDias = 3;

  seguradoras: string[] = [];
  responsaveis: string[] = [];

  wipTotal = 0;
  wipAtraso = 0;
  wipParados = 0;
  wipNoPrazo = 0;
  entradasPeriodo = 0;
  saidasPeriodo = 0;

  stageSummaries: StageSummary[] = [];
  personSummaries: PersonSummary[] = [];
  selectedStage: string | null = null;
  selectedResponsavel: string | null = null;

  entradasPorDia: DailyCount[] = [];
  saidasPorDia: DailyCount[] = [];
  saldoPeriodo = 0;

  activeTab: 'etapa' | 'responsavel' | 'fila' = 'etapa';

  private subscription = new Subscription();

  constructor(private workflowsService: WorkflowsService) {}

  ngOnInit(): void {
    const today = new Date();
    const endStr = this.toDateInput(today);
    const start = new Date(today);
    start.setDate(start.getDate() - 30);
    const startStr = this.toDateInput(start);
    this.filterStart = startStr;
    this.filterEnd = endStr;

    this.subscription.add(
      this.workflowsService.getAllDetails().subscribe((workflows) => {
        this.workflows = workflows;
        this.seguradoras = this.buildList((wf) => wf.seguradoraNome);
        this.responsaveis = this.buildList((wf) => wf.responsavelAtual);
        this.applyFilters();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  applyFilters(): void {
    const start = this.filterStart ? new Date(`${this.filterStart}T00:00:00`) : null;
    const end = this.filterEnd ? new Date(`${this.filterEnd}T23:59:59`) : null;
    const now = new Date();

    this.filtered = this.workflows.filter((wf) => {
      const inicio = this.getDataInicio(wf);
      if (start && inicio < start) return false;
      if (end && inicio > end) return false;

      if (this.filterSeguradora !== 'TODOS' && (wf.seguradoraNome ?? '—') !== this.filterSeguradora) {
        return false;
      }
      if (this.filterResponsavel !== 'TODOS' && (wf.responsavelAtual ?? '—') !== this.filterResponsavel) {
        return false;
      }
      if (this.filterStatus !== 'TODOS' && !this.matchStatus(wf, this.filterStatus, now)) {
        return false;
      }
      if (this.onlyWip && this.isFinalizado(wf)) {
        return false;
      }
      return true;
    });

    this.rebuildSummary();
  }

  private rebuildSummary(): void {
    const now = new Date();
    this.wipItems = this.filtered.filter((wf) => !this.isFinalizado(wf));

    this.wipTotal = this.wipItems.length;
    this.wipAtraso = this.wipItems.filter((wf) => this.isAtrasado(wf, now)).length;
    this.wipParados = this.wipItems.filter((wf) => this.isParado(wf, now)).length;
    this.wipNoPrazo = Math.max(0, this.wipTotal - this.wipAtraso);

    this.entradasPeriodo = this.filtered.filter((wf) => this.isEntradaNoPeriodo(wf)).length;
    this.saidasPeriodo = this.filtered.filter((wf) => this.isSaidaNoPeriodo(wf)).length;
    this.saldoPeriodo = this.entradasPeriodo - this.saidasPeriodo;

    this.stageSummaries = this.buildStageSummaries(this.wipItems, now);
    this.personSummaries = this.buildPersonSummaries(this.wipItems, now);
    this.selectedStage = this.selectedStage ?? (this.stageSummaries[0]?.etapa ?? null);
    this.selectedResponsavel =
      this.selectedResponsavel ?? (this.personSummaries[0]?.responsavel ?? null);

    this.entradasPorDia = this.buildDailyCounts(this.filtered, (wf) => this.getDataInicio(wf));
    this.saidasPorDia = this.buildDailyCounts(this.filtered, (wf) => this.getDataFinalReal(wf));
  }

  private buildStageSummaries(items: WorkflowDetalhe[], now: Date): StageSummary[] {
    const map = new Map<string, WorkflowDetalhe[]>();
    items.forEach((wf) => {
      const etapa = this.getEtapaAtualLabel(wf);
      if (!map.has(etapa)) map.set(etapa, []);
      map.get(etapa)!.push(wf);
    });

    const summaries: StageSummary[] = Array.from(map.entries()).map(([etapa, list]) => {
      const atrasados = list.filter((wf) => this.isAtrasado(wf, now)).length;
      const parados = list.filter((wf) => this.isParado(wf, now)).length;
      const idadeMedia = this.calcMedia(list.map((wf) => this.getDiasNaEtapa(wf, now)));
      const criticos = [...list]
        .sort((a, b) => {
          const atrasoDiff = this.getAtrasoDias(b, now) - this.getAtrasoDias(a, now);
          if (atrasoDiff !== 0) return atrasoDiff;
          return this.getDiasNaEtapa(b, now) - this.getDiasNaEtapa(a, now);
        })
        .slice(0, 3);
      return {
        etapa,
        items: list,
        wip: list.length,
        atrasados,
        parados,
        idadeMedia,
        criticos,
      };
    });

    return summaries.sort((a, b) => b.wip - a.wip);
  }

  private buildPersonSummaries(items: WorkflowDetalhe[], now: Date): PersonSummary[] {
    const map = new Map<string, WorkflowDetalhe[]>();
    items.forEach((wf) => {
      const resp = wf.responsavelAtual || '—';
      if (!map.has(resp)) map.set(resp, []);
      map.get(resp)!.push(wf);
    });

    return Array.from(map.entries())
      .map(([responsavel, list]) => {
        const atrasados = list.filter((wf) => this.isAtrasado(wf, now)).length;
        const parados = list.filter((wf) => this.isParado(wf, now)).length;
        const etapaTop = this.getTopEtapa(list);
        return {
          responsavel,
          items: list,
          wip: list.length,
          atrasados,
          parados,
          etapaTop,
        };
      })
      .sort((a, b) => b.wip - a.wip);
  }

  private buildDailyCounts(
    items: WorkflowDetalhe[],
    getter: (wf: WorkflowDetalhe) => Date | null
  ): DailyCount[] {
    const map = new Map<string, number>();
    items.forEach((wf) => {
      const date = getter(wf);
      if (!date) return;
      const key = this.toDateInput(date);
      map.set(key, (map.get(key) ?? 0) + 1);
    });
    return Array.from(map.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => (a.date < b.date ? 1 : -1))
      .slice(0, 7);
  }

  private buildList(getter: (wf: WorkflowDetalhe) => string | undefined): string[] {
    const set = new Set<string>();
    this.workflows.forEach((wf) => {
      const value = getter(wf);
      if (value && value.trim()) {
        set.add(value.trim());
      }
    });
    return Array.from(set).sort();
  }

  private matchStatus(wf: WorkflowDetalhe, filter: StatusFilter, now: Date): boolean {
    if (filter === 'CONCLUIDO') return this.isFinalizado(wf);
    if (filter === 'ATRASADO') return !this.isFinalizado(wf) && this.isAtrasado(wf, now);
    if (filter === 'EM_ANDAMENTO') return !this.isFinalizado(wf) && !this.isAtrasado(wf, now);
    return true;
  }

  private getTopEtapa(items: WorkflowDetalhe[]): string {
    const map = new Map<string, number>();
    items.forEach((wf) => {
      const etapa = this.getEtapaAtualLabel(wf);
      map.set(etapa, (map.get(etapa) ?? 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '—';
  }

  getDataInicio(wf: WorkflowDetalhe): Date {
    return wf.criadoEm instanceof Date ? wf.criadoEm : new Date(wf.criadoEm);
  }

  getDataFinalPrevista(wf: WorkflowDetalhe): Date | null {
    if (wf.dataPrevistaEntrega) {
      return wf.dataPrevistaEntrega instanceof Date
        ? wf.dataPrevistaEntrega
        : new Date(wf.dataPrevistaEntrega);
    }
    const dates = wf.etapas
      .map((etapa) => etapa.previstaFim)
      .filter((date): date is Date => Boolean(date));
    if (!dates.length) return null;
    return new Date(Math.max(...dates.map((date) => date.getTime())));
  }

  getDataFinalReal(wf: WorkflowDetalhe): Date | null {
    const finalizado = wf.etapas.find((etapa) => etapa.tipo === WorkflowEtapaTipo.FINALIZADO);
    if (finalizado?.fim) {
      return finalizado.fim instanceof Date ? finalizado.fim : new Date(finalizado.fim);
    }
    return null;
  }

  isFinalizado(wf: WorkflowDetalhe): boolean {
    return wf.etapaAtual === WorkflowEtapaTipo.FINALIZADO || Boolean(this.getDataFinalReal(wf));
  }

  isAtrasado(wf: WorkflowDetalhe, now: Date): boolean {
    const prevista = this.getDataFinalPrevista(wf);
    return Boolean(prevista && now > prevista);
  }

  getAtrasoDias(wf: WorkflowDetalhe, now: Date): number {
    const prevista = this.getDataFinalPrevista(wf);
    if (!prevista) return 0;
    const diff = now.getTime() - prevista.getTime();
    return Math.max(0, Math.floor(diff / (24 * 60 * 60 * 1000)));
  }

  getDiasNaEtapa(wf: WorkflowDetalhe, now: Date): number {
    const etapa = wf.etapas.find((item) => item.tipo === wf.etapaAtual);
    const inicio = etapa?.inicio ? (etapa.inicio instanceof Date ? etapa.inicio : new Date(etapa.inicio)) : this.getDataInicio(wf);
    const diff = now.getTime() - inicio.getTime();
    return Math.max(0, Math.floor(diff / (24 * 60 * 60 * 1000)));
  }

  getDiasNaEtapaAtual(wf: WorkflowDetalhe): number {
    return this.getDiasNaEtapa(wf, new Date());
  }

  getAtrasoDiasAtual(wf: WorkflowDetalhe): number {
    return this.getAtrasoDias(wf, new Date());
  }

  get selectedStageItems(): WorkflowDetalhe[] {
    if (!this.selectedStage) {
      return [];
    }
    return this.stageSummaries.find((stage) => stage.etapa === this.selectedStage)?.items ?? [];
  }

  get selectedPersonItems(): WorkflowDetalhe[] {
    if (!this.selectedResponsavel) {
      return [];
    }
    return (
      this.personSummaries.find((person) => person.responsavel === this.selectedResponsavel)?.items ??
      []
    );
  }

  isParado(wf: WorkflowDetalhe, now: Date): boolean {
    const etapa = wf.etapas.find((item) => item.tipo === wf.etapaAtual);
    const base =
      (etapa?.inicio ? (etapa.inicio instanceof Date ? etapa.inicio : new Date(etapa.inicio)) : null) ??
      this.getDataInicio(wf);
    const diff = now.getTime() - base.getTime();
    return Math.floor(diff / (24 * 60 * 60 * 1000)) >= this.paradoDias;
  }

  isEntradaNoPeriodo(wf: WorkflowDetalhe): boolean {
    if (!this.filterStart || !this.filterEnd) return true;
    const start = new Date(`${this.filterStart}T00:00:00`);
    const end = new Date(`${this.filterEnd}T23:59:59`);
    const inicio = this.getDataInicio(wf);
    return inicio >= start && inicio <= end;
  }

  isSaidaNoPeriodo(wf: WorkflowDetalhe): boolean {
    if (!this.filterStart || !this.filterEnd) return false;
    const real = this.getDataFinalReal(wf);
    if (!real) return false;
    const start = new Date(`${this.filterStart}T00:00:00`);
    const end = new Date(`${this.filterEnd}T23:59:59`);
    return real >= start && real <= end;
  }

  getEtapaAtualLabel(wf: WorkflowDetalhe): string {
    const etapa = wf.etapas.find((item) => item.tipo === wf.etapaAtual);
    return etapa?.nome ?? String(wf.etapaAtual);
  }

  formatOsNumero(wf: WorkflowDetalhe): string {
    return wf.osNumero || `OS-${String(wf.id).padStart(6, '0')}`;
  }

  formatVeiculo(wf: WorkflowDetalhe): string {
    return [wf.placa, wf.marca, wf.modelo].filter(Boolean).join(' ');
  }

  formatDateLabel(value: string): string {
    const date = new Date(`${value}T00:00:00`);
    return date.toLocaleDateString('pt-BR');
  }

  private calcMedia(values: number[]): number {
    if (!values.length) return 0;
    return Math.round(values.reduce((acc, value) => acc + value, 0) / values.length);
  }

  private toDateInput(date: Date): string {
    return date.toISOString().slice(0, 10);
  }
}

